import { useQuery } from '@tanstack/react-query';
import { type ComponentProps, type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { usePreviousDistinct } from 'react-use';

import useAssets from '~hooks/useAssets';
import useDelayFn from '~hooks/useDelayFn';
import { queries } from '~modules/media-library/controller/api/Queries';
import { type AssetId } from '~modules/media-library/model/Asset';
import { type AssetActions } from '~modules/media-library/model/AssetActions';
import { DetailModal } from '~modules/media-library/view/DetailModal/DetailModal';

type DetailModalControllerProps = Resolve<
  {
    AssetActions: AssetActions;
    assetId: AssetId;
    nextAssetId?: AssetId;
    prevAssetId?: AssetId;
  } & Pick<ComponentProps<typeof DetailModal>, 'onClose'>
>;

export function DetailModalController({
  assetId,
  onClose,
  AssetActions,
  nextAssetId,
  prevAssetId,
}: DetailModalControllerProps) {
  const { getAssetById, setAsset } = useAssets();
  const [isOpen, setIsOpen] = useState(false);
  const delayedOnClose = useDelayFn(onClose, 500);

  // get asset from store
  const asset = getAssetById(assetId);
  const prevAsset = usePreviousDistinct(asset, (_, next) => !Boolean(next));

  // load details and update asset in asset store
  const { data: assetData } = useQuery({
    ...queries.mediaLibrary.details(assetId),
    enabled: Boolean(assetId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (assetData === undefined) return;
    if (assetData) setAsset(assetData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetData]);

  // open details modal when a asset is set
  useEffect(() => {
    setIsOpen(Boolean(asset));
  }, [asset]);

  // Handler: close dialog, but delay the closing to have smoother transitions
  const handleOnClose = useCallback(() => {
    setIsOpen(false);
    delayedOnClose();
  }, [delayedOnClose]);

  const handleOnNext = useMemo(() => {
    if (!nextAssetId) return undefined;
    return function handleOnNext(evt: MouseEvent<HTMLElement>) {
      evt.preventDefault();
      evt.stopPropagation();
      AssetActions.onDetails?.(nextAssetId);
    };
  }, [AssetActions, nextAssetId]);

  const handleOnPrev = useMemo(() => {
    if (!prevAssetId) return undefined;
    return function handleOnPrev(evt: MouseEvent<HTMLElement>) {
      evt.preventDefault();
      evt.stopPropagation();
      AssetActions.onDetails?.(prevAssetId);
    };
  }, [AssetActions, prevAssetId]);

  return (
    <DetailModal
      isOpen={isOpen}
      asset={asset || prevAsset}
      onClose={handleOnClose}
      AssetActions={AssetActions}
      onNext={handleOnNext}
      onPrev={handleOnPrev}
    />
  );
}
